import { useCookies } from 'react-cookie'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Grid, Link, SxProps, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMappedTranslation } from 'hooks'
import LanguageDropdown from 'pages/dashboard/language/language-dropdown'
import { EVENT_KEYS } from 'utils/constants'
import i18n, { getFullNameByLocale, isRtl } from 'utils/i18n'

import IconArrow from './assets/images/arrow.svg'
import LogoFrance from './assets/images/logo-fr.svg'
import LogoJapan from './assets/images/logo-ja.svg'
import LogoHongkong from './assets/images/logo-zh-HK.svg'
import LogoTaiwan from './assets/images/logo-zh-TW.svg'
import LogoChina from './assets/images/logo-zh.svg'
import LogoGlobal from './assets/images/logo.svg'
import CookieAgreement from './cookie-agreement'
import { AutomationTagIds } from '../../assets/test/test-automation-tag-id'

function Footer() {
  const theme = useTheme()
  const mappedTranslation = useMappedTranslation()
  const { language } = i18n

  const [cookies] = useCookies()
  const stateCookieName = process.env.REACT_APP_STATE_COOKIE_NAME as string
  const isLoggedIn = cookies[stateCookieName] !== undefined

  const languageAndLocation = getFullNameByLocale(language)

  const currentPath = window.location.hostname
  const cnDomainRegx = /.cn$/

  const navigate = useNavigate()
  const location = useLocation()
  const currentRoute = location.pathname.toLowerCase()
  const isDashboardIntro = currentRoute.startsWith('/dashboard/intro')
  const isFamilyIntro = currentRoute === '/dashboard/family-intro'
  const isDarkMode = theme.palette.mode === 'dark'

  const FOOTER_URL = {
    TERMS_CONDITIONS: '/policies/terms-conditions',
    PRIVACY: '/policies/privacy-notices',
    NOTICE: '/notices',
    LATEST: '/latest'
  }

  let Logo = LogoGlobal

  switch (i18n.language) {
    case 'fr-FR':
      Logo = LogoFrance
      break

    case 'ja-JP':
      Logo = LogoJapan
      break

    case 'zh-HK':
      Logo = LogoHongkong
      break

    case 'zh-TW':
      Logo = LogoTaiwan
      break

    case 'zh-CN':
      Logo = LogoChina
      break

    default:
      Logo = LogoGlobal
      break
  }

  const footerTextStyle: SxProps = {
    color: isDarkMode ? 'text.primary' : { xs: 'text.secondary', md: 'text.primary' },
    fontSize: '12px',
    lineHeight: '24px',
    fontWeight: { xs: '300', md: '500' },
    display: 'flex',
    alignItems: 'center'
  }

  const footerTextCopyRightStyle: SxProps = {
    color: 'info.dark',
    fontSize: { xs: '10px', md: '12px' },
    lineHeight: { xs: '12px', md: '14px' },
    fontWeight: '500'
  }

  const footerLinkDivStyle: SxProps = {
    cursor: 'pointer',
    height: '14px'
  }

  const isCnDomain = () => {
    return cnDomainRegx.test(currentPath)
  }

  const handleLanguageAndLocation = () => {
    if (!isCnDomain()) {
      navigate(`/language?back=${location.pathname}`, {
        state: { ...location.state }
      })
    }
  }

  const handleTermCondition = () => {
    navigate(FOOTER_URL.TERMS_CONDITIONS + FOOTER_URL.LATEST)
  }

  const handlePrivacy = () => {
    navigate(FOOTER_URL.PRIVACY + FOOTER_URL.LATEST)
  }

  const handleNotice = () => {
    navigate(FOOTER_URL.NOTICE)
  }

  const handleContactCS = () => {
    window.location.href = `${process.env.REACT_APP_HELP_URL}`
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          marginTop: 'auto',
          backgroundColor:
            isDashboardIntro || isFamilyIntro
              ? theme.palette.mode === 'dark'
                ? theme.palette.common.black
                : theme.palette.common.white
              : 'transparent'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            maxWidth: '1420px',
            margin: 'auto',
            alignItems: {
              xs: 'center',
              md: 'end'
            },
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            padding: {
              xs: '30px 24px',
              md: '26px 128px 48px'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              alignItems: {
                xs: 'center',
                md: 'start'
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}
            >
              {isLoggedIn ? (
                <LanguageDropdown cnDomainRegx={cnDomainRegx} isRtl={isRtl} currentPath={currentPath} iconArrow={IconArrow} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: !isCnDomain() ? 'pointer' : ''
                  }}
                  data-testid={AutomationTagIds['dashboard.languagelocation.select']}
                  tabIndex={!isCnDomain() ? 0 : -1}
                  onClick={handleLanguageAndLocation}
                  onKeyDown={(e) => {
                    if (e.key === EVENT_KEYS.ENTER || e.key === EVENT_KEYS.SPACE) {
                      e.preventDefault()
                      handleLanguageAndLocation()
                    }
                  }}
                >
                  <Link
                    component='button'
                    type='button'
                    underline='none'
                    tabIndex={-1}
                    sx={{
                      cursor: !isCnDomain() ? 'pointer' : '',
                      fontSize: { xs: '12px', md: '14px' },
                      lineHeight: '24px'
                    }}
                    color='text.primary'
                    fontWeight='bold'
                    data-testid='Link7400706D'
                  >
                    {languageAndLocation}
                  </Link>
                  {!isCnDomain() && (
                    <Box
                      component='img'
                      alt=''
                      src={IconArrow}
                      sx={{
                        width: '24px',
                        height: '24px',
                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : null,
                        transform: isRtl ? 'scaleX(-1)' : ''
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Grid
              container
              sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start'
                },
                marginTop: { xs: '24px', md: '16px' },
                columnGap: { xs: '16px', md: '32px' },
                rowGap: { xs: '12px' }
              }}
              data-testid={AutomationTagIds['dashboard.footer.select']}
            >
              <Box sx={footerLinkDivStyle}>
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={handleTermCondition}
                  sx={{ ...footerTextStyle, cursor: 'pointer' }}
                  data-testid='BoxBF132A84'
                >
                  {mappedTranslation('footer.termsAndConditions')}
                </Link>
              </Box>
              <Box sx={footerLinkDivStyle}>
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={handlePrivacy}
                  sx={{ ...footerTextStyle, fontWeight: '700', cursor: 'pointer' }}
                  data-testid='BoxE01549E5'
                >
                  {mappedTranslation('footer.privacyPolicy')}
                </Link>
              </Box>
              <CookieAgreement />
              <Box sx={footerLinkDivStyle}>
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={handleNotice}
                  sx={{ ...footerTextStyle, cursor: 'pointer' }}
                  data-testid='Box325C36C6'
                >
                  {mappedTranslation('footer.notices')}
                </Link>
              </Box>
              <Box sx={footerLinkDivStyle}>
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={handleContactCS}
                  sx={{ ...footerTextStyle, cursor: 'pointer' }}
                  data-testid='Box050DCED9'
                >
                  {isLoggedIn ? mappedTranslation('footer.contactUs') : mappedTranslation('footer.customerSupport')}
                </Link>
              </Box>
            </Grid>
          </Box>
          <Box
            sx={{
              marginTop: { xs: '22px', md: '0px' },
              rowGap: { xs: '8px', md: '8px' },
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'column',
              alignItems: {
                xs: 'center',
                md: language === 'de-AT' ? 'center' : 'flex-end'
              }
            }}
            data-testid={AutomationTagIds['dashboard.copyright.select']}
          >
            <Box
              component='img'
              sx={{
                height: { xs: '16px', md: '24px' },
                minWidth: { xs: '101px', md: '152px' },
                opacity: '0.56'
              }}
              alt=''
              src={Logo}
            />
            <Typography
              sx={{
                ...footerTextCopyRightStyle,
                minWidth: { xs: '277px', md: 'max-content' },
                lineHeight: { xs: '12px', md: '14px' }
              }}
              color='text.secondary'
            >
              {mappedTranslation('iam.01.Common.Footer.All.Rights.Reserved', { 0: new Date().getFullYear() })}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default Footer
